import { DataType } from '@/modules/terra';
import snakeCase from 'lodash/fp/snakeCase';
import camelCase from 'lodash/fp/camelCase';

export enum GraphType {
  STEPS = 'graphType_steps',
  HR = 'graphType_hr',
  OXYGEN = 'graphType_oxygen',
  SLEEP_DURATION = 'graphType_sleepDuration',
  CUSTOM = 'graphType_custom'
}

export type PredefinedGraph = Exclude<GraphType, GraphType.CUSTOM>;

export class GraphService {
  private static readonly dataTypePerGraph: Record<PredefinedGraph, DataType> =
    {
      [GraphType.STEPS]: 'daily',
      [GraphType.HR]: 'daily',
      [GraphType.OXYGEN]: 'daily',
      [GraphType.SLEEP_DURATION]: 'sleep'
    };

  private static readonly dataFieldsPerGraph: Record<
    PredefinedGraph,
    string[]
  > = {
    [GraphType.STEPS]: ['data.distanceData.steps'],
    [GraphType.HR]: [
      'data.heartRateData.summary.avgHrBpm',
      'data.heartRateData.summary.minHrBpm',
      'data.heartRateData.summary.maxHrBpm',
      'data.heartRateData.summary.restingHrBpm'
    ],
    [GraphType.OXYGEN]: ['data.oxygenData.avgSaturationPercentage'],
    [GraphType.SLEEP_DURATION]: [
      'data.sleepDurationsData.asleep.durationAsleepStateSeconds',
      'data.sleepDurationsData.asleep.durationLightSleepStateSeconds',
      'data.sleepDurationsData.asleep.durationDeepSleepStateSeconds',
      'data.sleepDurationsData.asleep.durationRemSleepStateSeconds'
    ]
  };

  private static readonly dataFieldsPerGraphSnake =
    this.createDataFieldsPerGraphSnake();

  public static getDataType(graphType: PredefinedGraph | undefined) {
    if (!this.isPredefined(graphType)) {
      return undefined;
    }
    return this.dataTypePerGraph[graphType];
  }

  public static getDataFieldsForGraph(
    graphType: PredefinedGraph | undefined,
    snake = false
  ): string[] {
    if (!this.isPredefined(graphType)) {
      return [];
    }
    if (snake) {
      return this.dataFieldsPerGraphSnake[graphType];
    }
    return this.dataFieldsPerGraph[graphType];
  }

  public static isPredefined(
    graphType: GraphType | undefined
  ): graphType is PredefinedGraph {
    return !!graphType && graphType !== GraphType.CUSTOM;
  }

  public static pathToSnake(camelCasePath: string) {
    let snakeCasePath = camelCasePath.split('.').map(snakeCase).join('.');
    if (snakeCasePath.includes('rem_sleep')) {
      snakeCasePath = snakeCasePath.replace('rem_sleep', 'REM_sleep');
    }
    return snakeCasePath;
  }

  public static pathToCamel(snakeCasePath: string) {
    return snakeCasePath.split('.').map(camelCase).join('.');
  }

  private static createDataFieldsPerGraphSnake() {
    return Object.entries(this.dataFieldsPerGraph).reduce(
      (res, [key, fields]) => ({
        ...res,
        [key]: fields.map((camelPath) => this.pathToSnake(camelPath))
      }),
      this.dataFieldsPerGraph
    );
  }
}
